import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { dedupedGet } from '../lib/dedupedFetch';
import {
  installAxiosBearerBridge,
  saveTokensFromResponse,
  clearTokens,
} from '../lib/tokenBridge';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Bearer-token bridge (Jun 2026 — Safari iOS / ITP cross-site cookie block)
// ---------------------------------------------------------------------------
// Installs a global axios interceptor that:
//   1. Attaches `Authorization: Bearer <token>` on every `/api/*` call when
//      a token is in localStorage.
//   2. On 401, performs a one-shot refresh (POST /api/auth/refresh with
//      `{refresh_token}` in body for Safari, falling back to the cookie
//      for Chrome) and retries the original request.
// Cookies are kept untouched so Chrome / desktop users keep working.
installAxiosBearerBridge(axios, API_URL);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Sprint 4 QA fix (Feb 2026) — whenever an authenticated user is
  // present (fresh login OR restored session on cold boot inside the
  // Capacitor Wrapper), (re-)register the FCM device token with the
  // backend. This is idempotent server-side (upsert by device_id).
  // No-op on the web (mobileBridge.registerPushToken short-circuits
  // when Capacitor is absent).
  useEffect(() => {
    if (!user || user === false) return;
    (async () => {
      try {
        const bridge = await import('../lib/mobileBridge');
        if (bridge?.registerPushToken) {
          await bridge.registerPushToken({ force: true });
        }
      } catch (_e) { /* web / bridge absent — ignore */ }
    })();
  }, [user]);

  // Jun 23, 2026 — silent refresh loop.
  // Backend access_token now lives 60 minutes. We proactively call
  // /api/auth/refresh every 50 minutes so the cookie is replaced
  // BEFORE the old one expires — the user never gets a 401 / never
  // sees the "session expired" page just for leaving the tab idle.
  //
  // Also kicks on `visibilitychange` → visible so a tab the user
  // returned to after sleep refreshes immediately. The reactive 401
  // retry in tokenBridge.js still catches the rare race where this
  // proactive call missed (network drop, etc.).
  useEffect(() => {
    if (!user || user === false) return undefined;
    const refresh = async () => {
      try {
        const r = await axios.post(
          `${API_URL}/api/auth/refresh`,
          {},
          { withCredentials: true },
        );
        // Persist the new bearer for Safari ITP fallback.
        if (r?.data) saveTokensFromResponse(r.data);
      } catch (e) {
        // 401 here means the refresh token itself expired (30 days
        // idle) OR the user was disabled server-side. Either way the
        // next /api/* call will 401 and the AuthProvider will land
        // back at the login screen.
        console.warn('[auth] silent refresh failed', e?.response?.status || e?.message);
      }
    };
    const SILENT_REFRESH_MS = 50 * 60 * 1000;
    const id = setInterval(refresh, SILENT_REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  const checkAuth = async ({ force = false } = {}) => {
    try {
      // Jun 29, 2026 — MED-03 fix. Skip the /auth/me probe when there
      // is no signal that a session might exist. This eliminates the
      // 2× 401 devtools noise every fresh visitor sees on the login
      // page and also saves an unnecessary round-trip on cold loads.
      // Session hints in priority order:
      //   1. bearer token present in localStorage (Safari-ITP path)
      //   2. session hint flag set by successful login (any browser)
      //   3. `mesp_session_hint` cookie set by the login endpoint
      // If none is present we're definitively logged out.
      let hasSessionHint = false;
      try {
        if (typeof localStorage !== 'undefined') {
          // Jul 2026 — key must match `ACCESS_KEY` in lib/tokenBridge.js
          // (`mesp_access_token`). A stale refactor left this file
          // reading `mesp_bearer_access`, which was never written by
          // anyone → the session hint was always false, so freshly
          // registered users (whose only signal is localStorage tokens,
          // not the `mesp_session_hint` cookie or flag) landed on
          // /login instead of /dashboard. That was the "auto-login
          // broken after registration" regression the user reported.
          if (localStorage.getItem('mesp_access_token')) hasSessionHint = true;
          if (!hasSessionHint && localStorage.getItem('mesp_session_hint') === '1') {
            hasSessionHint = true;
          }
        }
        if (!hasSessionHint && typeof document !== 'undefined'
            && document.cookie && document.cookie.includes('mesp_session_hint=1')) {
          hasSessionHint = true;
        }
      } catch (_e) { /* ignore */ }
      if (!hasSessionHint && !force) {
        setUser(false);
        setLoading(false);
        return false;
      }
      const response = await dedupedGet(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      // Aug 2026 — Offline Data Resilience: snapshot the identity so a
      // cold boot WITHOUT network can restore the session instead of
      // bouncing the user to /login (see catch below).
      try { localStorage.setItem('mesp_user_snapshot', JSON.stringify(response.data)); } catch (_e) { /* ignore */ }
      return response.data;
    } catch (error) {
      // Aug 2026 — distinguish a REAL rejection (401/403 → logged out)
      // from a TRANSIENT failure. Transient = no response (offline) OR
      // a 5xx/429/502/503 while the server restarts during a deployment.
      // Kicking users to /login on a 503 was the "everyone got logged
      // out after the deploy" bug — their cookies/tokens were still
      // perfectly valid. Restore the last known identity instead.
      const status = error?.response?.status;
      const realRejection = status === 401 || status === 403;
      if (!realRejection) {
        try {
          const snap = JSON.parse(localStorage.getItem('mesp_user_snapshot') || 'null');
          if (snap && snap.id) {
            setUser(snap);
            setLoading(false);
            return snap;
          }
        } catch (_e) { /* corrupt snapshot — fall through */ }
      } else {
        // Aug 2026 prod-health fix — the session is definitively dead:
        // clear every hint so the NEXT cold boot skips the /auth/me
        // probe entirely (was producing a 401 on every app open for
        // users with an expired session — 93×401/day in prod).
        try {
          clearTokens();
          localStorage.removeItem('mesp_session_hint');
          localStorage.removeItem('mesp_user_snapshot');
          document.cookie = 'mesp_session_hint=; Max-Age=0; path=/';
        } catch (_e) { /* ignore */ }
      }
      setUser(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password) => {
    // Sep 2026 — Auth Clear Logic: a fresh login attempt must never ride
    // on stale/expired Bearer tokens left over from a previous session.
    // Clearing them here guarantees the attempt is evaluated purely on
    // the submitted credentials (fixes "second login attempt hangs").
    clearTokens();
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { identifier, password },
        { withCredentials: true }
      );
      setUser(response.data);
      // Jun 2026 — persist tokens for the Safari iOS / ITP fallback so
      // subsequent API calls succeed even when the cookie was blocked.
      saveTokensFromResponse(response.data);
      // 2026-07-26 · Multi-account snapshot — record this user in the
      // shared account switcher store so they show up in the picker
      // even if a follow-up page never mounts `useAccountSwitcher`.
      try {
        const store = await import('../lib/multiAccountStore');
        const snap = store.captureFromLoginResponse(response.data, response.data);
        if (snap) {
          await store.upsertAccount(snap);
          await store.setActiveUserId(snap.user_id);
        }
      } catch (_e) { /* IDB absent — non-fatal */ }
      // Jun 29, 2026 — MED-03: also store a lightweight session hint
      // so the next cold boot can skip the /auth/me probe when there
      // is definitely no session (avoids the pre-login 401 devtools
      // noise). Cleared on logout.
      try { localStorage.setItem('mesp_session_hint', '1'); } catch (_e) { /* ignore */ }
      try { localStorage.setItem('mesp_user_snapshot', JSON.stringify(response.data)); } catch (_e) { /* ignore */ }
      // Sprint 4 QA fix (Feb 2026) — FCM token registration is
      // triggered by the `useEffect([user])` above; setUser() has
      // already fired the state change so no explicit call is needed
      // here. Kept as a single source of truth for token lifecycle.
      return { success: true, user: response.data };
    } catch (error) {
      // 2FA required: backend returns 202 + detail.requires_2fa
      if (error.response?.status === 202 && error.response?.data?.detail?.requires_2fa) {
        return { success: false, requires_2fa: true };
      }
      // Network-layer failures have no `response` — surface a localized hint.
      if (!error.response) {
        return {
          success: false,
          errorType: 'network',
          error: 'تعذّر الاتصال بالخادم. تحقق من اتصالك بالإنترنت وحاول مرة أخرى.',
        };
      }
      const status = error.response.status;
      // Sep 2026 — a 5xx during a deployment/restart is NOT a wrong
      // password. Show a "server updating" message instead of the
      // misleading "فشل تسجيل الدخول" so users don't hammer retries
      // (which used to trip the brute-force lockout).
      if (status >= 500) {
        return {
          success: false,
          errorType: 'server',
          error: 'الخادم قيد التحديث حالياً. يرجى الانتظار لحظات ثم المحاولة مرة أخرى — بياناتك صحيحة ولم تُرفض.',
        };
      }
      const detail = error.response?.data?.detail;
      let message = 'Login failed';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      }
      if (status === 429) {
        return { success: false, errorType: 'locked', error: message };
      }
      // Real credential rejection (401/403) — make sure no stale session
      // hints survive so the next attempt starts 100% clean.
      try { localStorage.removeItem('mesp_session_hint'); } catch (_e) { /* ignore */ }
      try { localStorage.removeItem('mesp_user_snapshot'); } catch (_e) { /* ignore */ }
      return { success: false, errorType: 'credentials', error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        userData,
        { withCredentials: true }
      );
      // Jun 2026 — same Safari-iOS fallback for the register flow.
      saveTokensFromResponse(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Registration failed';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      }
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    // v1.1.0 fix #1 — unbind this device's FCM token from the account
    // BEFORE the session dies (the revoke call needs auth). No-op on web.
    try {
      const bridge = await import('../lib/mobileBridge');
      if (bridge?.revokePushToken) await bridge.revokePushToken();
    } catch (_e) { /* web / bridge absent — ignore */ }
    try {
      await axios.post(
        `${API_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Jun 2026 — drop the Safari-iOS Bearer fallback alongside the cookies.
      clearTokens();
      // Jun 29, 2026 — MED-03: clear the session hint so future cold
      // boots skip the /auth/me probe entirely.
      try { localStorage.removeItem('mesp_session_hint'); } catch (_e) { /* ignore */ }
      // Aug 2026 — drop the offline identity snapshot too.
      try { localStorage.removeItem('mesp_user_snapshot'); } catch (_e) { /* ignore */ }
      // v1.1.0 fix #6 — Deep Clear: expire the JS-visible session-hint
      // cookie too so no stale signal survives on this device.
      try { document.cookie = 'mesp_session_hint=; Max-Age=0; path=/'; } catch (_e) { /* ignore */ }
      // Sep 2026 — Force Logout (auth-loop fix): full wipe of
      // localStorage + sessionStorage + JS-visible cookies. No stale
      // signal survives to resurrect the session or auto-redirect.
      try {
        const { purgeUserScopedCaches, hardWipeBrowserState } = await import('../lib/sessionPurge');
        purgeUserScopedCaches();
        hardWipeBrowserState();
      } catch (_e) { /* ignore */ }
      // Jul 2026 — offline-first layer hygiene: drop the persisted
      // TanStack Query snapshot (IndexedDB) + in-memory cache so the
      // next account on this device never rehydrates another user's
      // data. Lazy imports keep the login bundle lean.
      try {
        const [{ clearPersistedCache }, { queryClient }] = await Promise.all([
          import('../lib/query/persister'),
          import('../lib/query/queryClient'),
        ]);
        queryClient.clear();
        await clearPersistedCache();
      } catch (_e) { /* ignore */ }
      // 2026-07-26 · Multi-account — drop ONLY the currently active
      // account from the switcher store. Other stored accounts stay
      // so the user can hop back into them without re-authenticating.
      try {
        const uid = user && user !== false ? (user.id || user._id || user.user_id) : null;
        if (uid) {
          const store = await import('../lib/multiAccountStore');
          await store.removeAccount(String(uid));
        }
      } catch (_e) { /* IDB absent — non-fatal */ }
      setUser(false);
    }
  };

  // Granular permission helper. Returns true when the active user has the
  // requested permission. Super_Admin (or any user whose effective profile
  // has the key === true) passes. Falls back to a permissive default for the
  // legacy `user.permissions` array shape (back-compat with older code).
  const hasPermission = (key) => {
    if (!user || user === false) return false;
    if (user.role === 'Super_Admin') return true;
    const profile = user.permission_profile || {};
    if (profile && typeof profile === 'object' && key in profile) {
      return !!profile[key];
    }
    // Legacy fallback — array-shaped permissions
    if (Array.isArray(user.permissions)) {
      return user.permissions.includes(key);
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};